/**
 * Viewer profile id cache.
 *
 * CORRECTION01 (R1 fix): a single, module-level subscription-free
 * cache for the viewer's own profile id. The viewer's profile id is
 * derived from the existing my-profile port (GET /sponsor/profile or
 * /friend/profile). The ProfileDetail Page wrapper subscribes to this
 * cache so that the value is available on the first render after
 * bootstrap, without an extra round-trip per page render.
 *
 * Topology:
 *   - Module-level cache (single key).
 *   - updateViewerProfileId(profileId) is called by the bootstrap
 *     hook OR by the ProfileDetail page wrapper on first mount.
 *   - useViewerProfileId() returns the cached id (or null if not yet
 *     fetched) and subscribes to changes via a small hook.
 *
 * SECURITY:
 *   - This cache is read-only from the outside (only the bootstrap
 *     path writes it).
 *   - The value is a UUID; no PII is exposed.
 *   - The cache is process-local; never persisted, never logged.
 */

import { useEffect, useState } from 'react';

let cachedViewerProfileId: string | null = null;
type Listener = (id: string | null) => void;
const listeners = new Set<Listener>();

/**
 * Update the cached viewer profile id. Called by the bootstrap
 * path after the viewer profile is fetched via the my-profile port.
 * Pass null to invalidate (e.g., on logout).
 */
export function updateViewerProfileId(profileId: string | null): void {
  if (cachedViewerProfileId === profileId) return;
  cachedViewerProfileId = profileId;
  for (const listener of listeners) {
    listener(profileId);
  }
}

/**
 * Read the current cached viewer profile id synchronously.
 * Useful for code paths that do not need to react to changes
 * (e.g., comparisons in stable callbacks).
 */
export function getViewerProfileId(): string | null {
  return cachedViewerProfileId;
}

/**
 * Subscribe to the cached viewer profile id.
 * Returns the current value and re-renders on changes.
 */
export function useViewerProfileId(): string | null {
  const [id, setId] = useState<string | null>(cachedViewerProfileId);
  useEffect(() => {
    const listener: Listener = (next) => setId(next);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return id;
}
